/**
 * ============================================================
 * BOHO TALES — YOU MAY ALSO LIKE
 * ============================================================
 *
 * FINAL PRIORITY
 *
 * 1. FOCUS
 *    - Always first priority when active/relevant
 *
 * 2. MANUAL RECOMMENDATIONS
 *    - Source: custom.you_may_also_like
 *    - Exact metafield order is preserved
 *
 * 3. SHOPIFY RECOMMENDATIONS
 *    - Whatever Shopify returns
 *    - Used to fill remaining slots
 *
 * RULES
 * - Current PDP is excluded
 * - Duplicates are removed
 * - Final result is limited to maxProducts
 * - No same-collection fallback
 * - Manual recommendations are not collection restricted
 * - Shopify recommendations are not collection restricted
 * ============================================================
 */

(function () {

  const FOCUS_DURATION = 30 * 60 * 1000;


  /* =========================================================
     START
     ========================================================= */

  function init() {

    const sections = document.querySelectorAll(
      '[data-boho-yml]:not([data-boho-yml-initialized])'
    );

    sections.forEach((section) => {

      section.setAttribute(
        'data-boho-yml-initialized',
        'true'
      );

      loadRecommendations(section);

    });

  }


  /* =========================================================
     MAIN FLOW
     ========================================================= */

  async function loadRecommendations(section) {

    const currentProductId =
      String(section.dataset.productId || '');

    const sectionId =
      section.dataset.sectionId;

    const maxProducts =
      Number(section.dataset.maxProducts || 4);

    const recommendationsUrl =
      section.dataset.recommendationsUrl;

    const recommendationCollection =
      String(
        section.dataset.recommendationCollection || ''
      ).trim();


    const loader =
      section.querySelector('[data-boho-yml-loader]');

    const finalGrid =
      section.querySelector('[data-boho-yml-final-grid]');


    if (
      !currentProductId ||
      !sectionId ||
      !recommendationsUrl ||
      !finalGrid
    ) {

      console.warn(
        'BOHO YML — missing required section data.'
      );

      if (loader) {
        loader.hidden = true;
      }

      return;

    }


    try {

      /* -----------------------------------------------------
         1. READ ACTIVE FOCUS
         ----------------------------------------------------- */

      const focusData =
        getActiveFocus();


      /*
       * The Focus journey is relevant to this PDP when its
       * originating master collection matches this PDP's
       * master collection.
       *
       * This check does NOT filter Focus products themselves.
       */

      const validFocus =
        Boolean(
          focusData &&
          recommendationCollection &&
          focusData.collectionHandle === recommendationCollection
        );


      /* -----------------------------------------------------
         2. GET FOCUS PRODUCTS
         ----------------------------------------------------- */

      let focusCards = [];


      if (validFocus) {

        const focusProducts =
          await getFocusProducts(
            focusData.collectionHandle,
            focusData.tag,
            currentProductId
          );


        const limitedFocusProducts =
          focusProducts.slice(
            0,
            maxProducts
          );


        focusCards =
          await Promise.all(
            limitedFocusProducts.map(
              (focusProduct) =>
                renderProductCard(
                  focusProduct,
                  sectionId
                )
            )
          );


        focusCards =
          focusCards.filter(Boolean);

      }


      /* -----------------------------------------------------
         3. GET MANUAL RECOMMENDATIONS

         These are already rendered by Liquid from:

         custom.you_may_also_like

         DOM order = metafield order.
         ----------------------------------------------------- */

      const manualCards =
        getManualRecommendations(
          section,
          currentProductId
        );


      /* -----------------------------------------------------
         4. GET SHOPIFY RECOMMENDATIONS

         Shopify is the final fallback source.

         No collection filtering is applied.
         ----------------------------------------------------- */

      const shopifyCards =
        await getShopifyRecommendations({
          recommendationsUrl,
          currentProductId,
          sectionId,
          maxProducts
        });


      /* -----------------------------------------------------
         5. FINAL MERGE

         Focus
         → Manual
         → Shopify
         ----------------------------------------------------- */

      const finalCards =
        mergeCards({
          focusCards,
          manualCards,
          shopifyCards,
          currentProductId,
          maxProducts
        });


      /* -----------------------------------------------------
         6. DISPLAY
         ----------------------------------------------------- */

      finalGrid.innerHTML = '';


      finalCards.forEach((card) => {

        finalGrid.appendChild(
          card.cloneNode(true)
        );

      });


      if (loader) {
        loader.hidden = true;
      }


      if (finalCards.length) {

        finalGrid.hidden = false;

      } else {

        section.hidden = true;

      }


      console.log(
        'BOHO YML — final recommendations:',
        {
          recommendationCollection,
          focus: focusData,
          validFocus,
          focusCount: focusCards.length,
          manualCount: manualCards.length,
          shopifyCount: shopifyCards.length,
          finalCount: finalCards.length
        }
      );


    } catch (error) {

      console.error(
        'BOHO YML — recommendation loading failed:',
        error
      );


      if (loader) {
        loader.hidden = true;
      }

    }

  }


  /* =========================================================
     MANUAL RECOMMENDATIONS
     ========================================================= */

  function getManualRecommendations(
    section,
    currentProductId
  ) {

    const cards =
      Array.from(
        section.querySelectorAll(
          '[data-boho-yml-manual-products] [data-boho-yml-manual-card]'
        )
      );


    const seen =
      new Set();


    return cards.filter((card) => {

      const productId =
        String(
          card.dataset.productId || ''
        );


      if (!productId) {
        return false;
      }


      if (productId === currentProductId) {
        return false;
      }


      if (seen.has(productId)) {
        return false;
      }


      seen.add(productId);

      return true;

    });

  }


  /* =========================================================
     SHOPIFY RECOMMENDATIONS
     ========================================================= */

  async function getShopifyRecommendations({
    recommendationsUrl,
    currentProductId,
    sectionId,
    maxProducts
  }) {

    const url =
      `${recommendationsUrl}` +
      `?product_id=${encodeURIComponent(currentProductId)}` +
      `&limit=${encodeURIComponent(maxProducts)}` +
      `&section_id=${encodeURIComponent(sectionId)}` +
      `&intent=related`;


    const response =
      await fetch(url, {
        credentials: 'same-origin'
      });


    if (!response.ok) {

      throw new Error(
        `Shopify recommendations failed: ${response.status}`
      );

    }


    const html =
      await response.text();


    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );


    return Array.from(
      doc.querySelectorAll(
        '[data-boho-yml-shopify-results] [data-boho-yml-card]'
      )
    );

  }


  /* =========================================================
     READ ACTIVE FOCUS
     ========================================================= */

  function getActiveFocus() {

    try {

      const journeyRaw =
        sessionStorage.getItem(
          'bohotales_active_focus_collection'
        );


      if (!journeyRaw) {
        return null;
      }


      let journey;


      try {

        journey =
          JSON.parse(journeyRaw);

      } catch {

        journey = {
          collection: journeyRaw
        };

      }


      const collectionHandle =
        journey?.collection ||
        journey?.collectionHandle;


      if (!collectionHandle) {
        return null;
      }


      const focusKey =
        `bohotales_focus_${collectionHandle}`;


      const focusRaw =
        sessionStorage.getItem(
          focusKey
        );


      if (!focusRaw) {
        return null;
      }


      let focus;


      try {

        focus =
          JSON.parse(focusRaw);

      } catch {

        return null;

      }


      if (
        !focus?.tag ||
        !focus?.expiresAt
      ) {

        return null;

      }


      if (focus.expiresAt <= Date.now()) {

        sessionStorage.removeItem(
          focusKey
        );

        return null;

      }


      if (
        focus.expiresAt >
        Date.now() + FOCUS_DURATION + 60000
      ) {

        console.warn(
          'BOHO YML — unusual Focus expiry detected.'
        );

      }


      return {
        collectionHandle,
        tag: focus.tag,
        expiresAt: focus.expiresAt
      };


    } catch (error) {

      console.warn(
        'BOHO YML — could not read Focus:',
        error
      );

      return null;

    }

  }


  /* =========================================================
     GET FOCUS PRODUCTS
     ========================================================= */

  async function getFocusProducts(
    collectionHandle,
    focusTag,
    currentProductId
  ) {

    try {

      const url =
        `/collections/` +
        `${encodeURIComponent(collectionHandle)}/` +
        `${encodeURIComponent(focusTag)}`;


      const response =
        await fetch(url, {
          credentials: 'same-origin'
        });


      if (!response.ok) {

        throw new Error(
          `Focus collection request failed: ${response.status}`
        );

      }


      const html =
        await response.text();


      const doc =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      return extractProductsFromCollectionPage(
        doc,
        currentProductId
      );


    } catch (error) {

      console.warn(
        'BOHO YML — Focus products failed:',
        error
      );

      return [];

    }

  }


  /* =========================================================
     EXTRACT PRODUCTS FROM FOCUS COLLECTION PAGE
     ========================================================= */

  function extractProductsFromCollectionPage(
    doc,
    currentProductId
  ) {

    const productItems =
      Array.from(
        doc.querySelectorAll(
          '.product-grid > .product-grid__item[data-product-id]'
        )
      );


    const seen =
      new Set();

    const products = [];


    for (const item of productItems) {

      const productId =
        String(
          item.dataset.productId || ''
        );


      if (!productId) {
        continue;
      }


      if (productId === currentProductId) {
        continue;
      }


      if (seen.has(productId)) {
        continue;
      }


      const link =
        item.querySelector(
          'a[href*="/products/"]'
        );


      if (!link) {
        continue;
      }


      const productUrl =
        new URL(
          link.href,
          window.location.origin
        );


      const match =
        productUrl.pathname.match(
          /^\/products\/([^/?#]+)/
        );


      if (!match) {
        continue;
      }


      seen.add(productId);


      products.push({
        id: productId,
        handle: match[1],
        url: `/products/${match[1]}`
      });

    }


    return products;

  }


  /* =========================================================
     RENDER FOCUS PRODUCT USING EXISTING PRODUCT CARD
     ========================================================= */

  async function renderProductCard(
    product,
    sectionId
  ) {

    try {

      const url =
        `${product.url}` +
        `?section_id=${encodeURIComponent(sectionId)}`;


      const response =
        await fetch(url, {
          credentials: 'same-origin'
        });


      if (!response.ok) {

        throw new Error(
          `Product card render failed: ${response.status}`
        );

      }


      const html =
        await response.text();


      const doc =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      const renderer =
        doc.querySelector(
          '[data-boho-yml-product-renderer]'
        );


      if (!renderer) {
        return null;
      }


      const card =
        renderer.querySelector(
          '[data-boho-yml-card]'
        );


      if (!card) {
        return null;
      }


      return card;


    } catch (error) {

      console.warn(
        `BOHO YML — could not render ${product.handle}:`,
        error
      );

      return null;

    }

  }


  /* =========================================================
     MERGE + DEDUPLICATE
     ========================================================= */

  function mergeCards({
    focusCards,
    manualCards,
    shopifyCards,
    currentProductId,
    maxProducts
  }) {

    const finalCards = [];

    const seen =
      new Set();


    /*
     * THIS ORDER DEFINES PRIORITY:
     *
     * Focus
     * → Manual metafield
     * → Shopify
     */

    const candidates = [
      ...focusCards,
      ...manualCards,
      ...shopifyCards
    ];


    for (const card of candidates) {

      const productId =
        String(
          card.dataset.productId || ''
        );


      if (!productId) {
        continue;
      }


      if (productId === currentProductId) {
        continue;
      }


      if (seen.has(productId)) {
        continue;
      }


      seen.add(productId);

      finalCards.push(card);


      if (
        finalCards.length >= maxProducts
      ) {
        break;
      }

    }


    return finalCards;

  }


  /* =========================================================
     INITIALIZE
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();

  }


  /*
   * Shopify Theme Editor can dynamically insert sections.
   */

  document.addEventListener(
    'shopify:section:load',
    init
  );


})();